import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BehaviorSubject } from 'rxjs';

export const CHIPS_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiInputComponent),
  multi: true,
};

@Component({
  selector: 'z-multi-input',
  templateUrl: './multi-input.component.html',
  providers: [CHIPS_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiInputComponent implements ControlValueAccessor {
  @Input() style: Record<string, any>;
  @Input() styleClass: string;

  @Input() disabled: boolean;
  @Input() readonly: boolean;

  @Input() label: string;
  @Input() hint: string;
  @Input() placeholder: string;

  @Input() max: number;
  @Input() tabindex: number;
  @Input() inputId: string;

  @Input() allowDuplicate: boolean = true;

  @Input() inputStyle: Record<string, any>;
  @Input() inputStyleClass: string;

  @Input() addOnTab: boolean = true;
  @Input() addOnBlur: boolean = true;
  @Input() separator: string | RegExp = /(,|;)/;
  @Input() clearButton: boolean = false;

  @Output() add: EventEmitter<{ originalEvent: Event; value: string }> = new EventEmitter();
  @Output() remove: EventEmitter<{ originalEvent: Event; value: string }> = new EventEmitter();
  @Output() focus: EventEmitter<FocusEvent> = new EventEmitter();
  @Output() blur: EventEmitter<FocusEvent> = new EventEmitter();
  @Output() click: EventEmitter<{ originalEvent: Event; value: string }> = new EventEmitter();
  @Output() clear: EventEmitter<void> = new EventEmitter();

  @ViewChild('inputtext') inputViewChild: ElementRef;

  value: string[];

  hasValue$ = new BehaviorSubject<boolean>(false);

  onModelChange: (value: string[]) => void;
  onModelTouched: () => void;

  valueChanged: boolean;
  hasFocus: boolean;
  filled: boolean;

  constructor(@Inject(DOCUMENT) private document: Document, public el: ElementRef, public cd: ChangeDetectorRef) {}

  onClick() {
    this.inputViewChild.nativeElement.focus();
  }

  onInput() {
    this.updateFilledState();
  }

  onPaste(event: ClipboardEvent) {
    if (!this.disabled) {
      if (this.separator) {
        const pastedData = (event.clipboardData || this.document.defaultView['clipboardData']).getData('Text');

        for (const val of pastedData.split(this.separator)) {
          this.addItem(event, val, true);
        }

        this.inputViewChild.nativeElement.value = '';
      }

      this.updateFilledState();
    }
  }

  updateFilledState() {
    if (!this.value || this.value.length === 0) {
      this.filled = this.inputViewChild && this.inputViewChild.nativeElement && this.inputViewChild.nativeElement.value != '';
    } else {
      this.filled = true;
    }
  }

  onItemClick(event: Event, item: string) {
    this.click.emit({
      originalEvent: event,
      value: item,
    });
  }

  writeValue(value: string[]) {
    this.value = value;

    if (this.value) {
      this.hasValue$.next(true);
    } else {
      this.hasValue$.next(false);
    }

    this.updateMaxedOut();
    this.updateFilledState();
    this.cd.markForCheck();
  }

  registerOnChange(fn: (value: string[]) => void) {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onModelTouched = fn;
  }

  setDisabledState(value: boolean) {
    this.disabled = value;
    this.cd.markForCheck();
  }

  updateModel(value: string[]) {
    this.value = value;

    if (this.onModelChange) {
      this.onModelChange(value);
    }

    if (this.onModelTouched) {
      this.onModelTouched();
    }
  }

  onInputFocus(event: FocusEvent) {
    this.hasFocus = true;
    this.focus.emit(event);
  }

  onInputBlur(event: FocusEvent) {
    this.hasFocus = false;

    if (this.addOnBlur && this.inputViewChild.nativeElement.value) {
      this.addItem(event, this.inputViewChild.nativeElement.value, false);
    }

    this.onModelTouched();
    this.blur.emit(event);
  }

  removeItem(event: Event, index: number): void {
    if (this.disabled) {
      return;
    }

    const removedItem = this.value[index];

    this.value = this.value.filter((_value: string, i: number) => i != index);
    this.updateModel(this.value);

    this.remove.emit({
      originalEvent: event,
      value: removedItem,
    });

    this.updateFilledState();
    this.updateMaxedOut();
  }

  addItem(event: Event, item: string, preventDefault: boolean): void {
    this.value = this.value || [];

    if (item && item.trim().length) {
      if (this.allowDuplicate || this.value.indexOf(item) === -1) {
        this.value = [...this.value, item];
        this.updateModel(this.value);
        this.add.emit({
          originalEvent: event,
          value: item,
        });
      }
    }

    this.updateFilledState();
    this.updateMaxedOut();

    this.inputViewChild.nativeElement.value = '';

    if (preventDefault) {
      event.preventDefault();
    }
  }

  onClear() {
    this.value = null;
    this.updateFilledState();
    this.updateModel(this.value);
    this.clear.emit();
  }

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Backspace':
        if (this.inputViewChild.nativeElement.value.length === 0 && this.value && this.value.length > 0) {
          this.value = [...this.value];
          const removedItem = this.value.pop();

          this.updateModel(this.value);

          this.remove.emit({
            originalEvent: event,
            value: removedItem,
          });

          this.updateFilledState();
        }
        break;

      case 'Enter':
        this.addItem(event, this.inputViewChild.nativeElement.value, true);
        break;

      case 'Tab':
        if (this.addOnTab && this.inputViewChild.nativeElement.value !== '') {
          this.addItem(event, this.inputViewChild.nativeElement.value, true);
        }
        break;

      default:
        if (this.max && this.value && this.max === this.value.length) {
          event.preventDefault();
        } else if (this.separator) {
          if (typeof this.separator === 'string') {
            if (event.key === this.separator) {
              this.addItem(event, this.inputViewChild.nativeElement.value, true);
            }
          } else {
            if (event.key.match(this.separator)) {
              this.addItem(event, this.inputViewChild.nativeElement.value, true);
            }
          }
        }
        break;
    }
  }

  updateMaxedOut(): void {
    if (this.inputViewChild && this.inputViewChild.nativeElement) {
      if (this.max && this.value && this.max === this.value.length) {
        // Calling `blur` is necessary because firefox does not call `focus` events
        // for disabled inputs, unlike chromium browsers.
        this.inputViewChild.nativeElement.blur();
        this.inputViewChild.nativeElement.disabled = true;
      } else {
        if (this.disabled) {
          this.inputViewChild.nativeElement.blur();
        }

        this.inputViewChild.nativeElement.disabled = this.disabled || false;
      }
    }
  }
}
